import { useState } from "react";
import { Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Shield, Loader2, ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";

const schema = z.object({
  email: z.string().email("Please enter a valid email address"),
});
type FormData = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  async function onSubmit(data: FormData) {
    setIsLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email }),
      });
      if (!res.ok) {
        const json = await res.json();
        toast({ title: "Failed", description: json.error?.message || json.message || "Something went wrong. Please try again.", variant: "destructive" });
        return;
      }
      setSentEmail(data.email);
      setSent(true);
    } catch {
      toast({ title: "Failed", description: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "var(--restore-dark-bg)" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center space-x-3 mb-10 justify-center">
          <div className="rounded-xl p-2" style={{ backgroundColor: "var(--restore-accent)" }}>
            <Shield className="w-6 h-6 text-gray-900" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">RestoreAI</h1>
            <p className="text-sm" style={{ color: "var(--restore-text-muted)" }}>AI-Powered Restoration Intelligence</p>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-700/50 bg-gray-900/80 p-8">
          {sent ? (
            <div className="text-center">
              <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center border border-green-500/30 mx-auto mb-6">
                <Mail className="w-7 h-7 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Check your inbox</h2>
              <p className="text-gray-400 text-sm mb-1">We sent a password reset link to</p>
              <p className="font-medium mb-6" style={{ color: "var(--restore-accent)" }}>{sentEmail}</p>
              <p className="text-gray-500 text-sm mb-8">The link expires in 1 hour. If you don't see it, check your spam folder.</p>
              <Link href="/login">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to sign in
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">Forgot password?</h2>
              <p className="text-gray-400 text-sm mb-8">Enter your email and we'll send you a link to reset your password.</p>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-300">Email address</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder="you@company.com"
                            autoComplete="email"
                            className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-green-400 h-11"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full text-gray-900 font-semibold h-11 text-base"
                    style={{ backgroundColor: "var(--restore-accent)" }}
                  >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send reset link"}
                  </Button>
                </form>
              </Form>

              <div className="mt-6 text-center">
                <Link href="/login" className="text-sm text-gray-400 hover:text-gray-200 flex items-center justify-center gap-1">
                  <ArrowLeft className="w-3 h-3" />
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
